import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useState, useRef } from "react";
import styled from "styled-components";

import { ClaimDetailsModal } from "../../../components/ClaimDetailsModal/ClaimDetailsModal";
import { parseDate } from "../../../helpers/date";
import useLocalStorageState from "../../../hooks/useLocalStorageState";
import {
  useGetClaimsQuery,
  useLazyExportClaimsQuery,
  useGetDistinctClassificationsQuery,
  useGetDistinctClearPayersQuery,
  useGetClaimByIdQuery,
} from "../../../store/queries/claims";
import {
  IClaim,
  CLAIM_CATEGORIES,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  RESOLUTION_STATUS_LABELS,
  RESOLUTION_STATUS_COLORS,
  RESOLUTION_FILTER_OPTIONS,
  PaymentType,
  PAYMENT_TYPE_OPTIONS,
} from "../../../types/claim";
import {
  calculateOOPPercent,
  extractClaimPatientResponsibility,
  formatDollars,
} from "../../../utils/claimAdjustments";

const CaseSignatureLink = styled.span`
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #0056b3;
  }
`;

const CompactDataTable = styled(DataTable)`
  /* Reduce cell padding */
  .p-datatable-tbody > tr > td {
    padding: 0.25rem 0.5rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
  }

  /* Reduce header padding and enable wrapping */
  .p-datatable-thead > tr > th {
    padding: 0.25rem 0.5rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    line-height: 1.3 !important;
  }

  /* Ensure column content wraps */
  .p-column-title {
    white-space: normal !important;
    word-wrap: break-word !important;
  }
`;

interface IClaimsListProps {
  accountId: string;
}

function ClaimsList({ accountId }: IClaimsListProps) {
  const toast = useRef<Toast>(null);

  // Filter state - non-persisted
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<Date[]>([]);

  // Payment types filter - default to all types selected
  const [paymentTypes, setPaymentTypes] = useLocalStorageState<PaymentType[]>(
    `clearhealth_claims_filters_paymentTypes_${accountId}`,
    ["underpayment", "overpayment", "correct"]
  );

  // Filter state - persisted to localStorage
  const [chargeBelowContractRateFilter, setChargeBelowContractRateFilter] =
    useLocalStorageState<"include" | "exclude" | "only">(
      `clearhealth_claims_filters_charge_${accountId}`,
      "include"
    );
  const [minDifferencePercent, setMinDifferencePercent] = useLocalStorageState<
    number | undefined
  >(`clearhealth_claims_filters_minPercent_${accountId}`, undefined);

  // Resolution status filter - default to "not_reviewed" to show unprocessed items
  const [resolutionFilter, setResolutionFilter] = useLocalStorageState<
    "not_reviewed" | "in_review" | "all"
  >(`clearhealth_claims_filters_resolution_${accountId}`, "not_reviewed");

  // AI Classification filter - default to "all" to show everything
  const [classificationFilter, setClassificationFilter] =
    useLocalStorageState<string>(
      `clearhealth_claims_filters_classification_${accountId}`,
      "all"
    );

  // Clear Payer filter - default to null (show all)
  const [clearPayerFilter, setClearPayerFilter] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Sorting state - default to difference ascending (most negative first = largest underpayments)
  const [sortField, setSortField] = useState("amountDifference");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IClaim | null>(null);

  // Navigation state for similar claims - maintains history stack for back navigation
  const [navigatedClaimId, setNavigatedClaimId] = useState<string | null>(null);
  const [claimHistory, setClaimHistory] = useState<IClaim[]>([]);

  // Export state
  const [exportTrigger, { isLoading: isExporting }] =
    useLazyExportClaimsQuery();

  // Fetch distinct classifications for the filter dropdown
  const { data: classificationsData } = useGetDistinctClassificationsQuery({
    accountId,
  });

  // Fetch distinct Clear payers for the filter dropdown
  const { data: clearPayersData } = useGetDistinctClearPayersQuery({
    accountId,
  });

  // Fetch navigated claim by ID (for similar claims navigation)
  const { data: navigatedClaimData, isFetching: isNavigatedClaimFetching } =
    useGetClaimByIdQuery(
      { claimId: navigatedClaimId! },
      { skip: !navigatedClaimId }
    );

  // Handle navigation to a similar claim - push current claim to history
  const handleNavigateToClaim = (claimId: string) => {
    if (selectedRow) {
      setClaimHistory((prev) => [...prev, selectedRow]);
    }
    setNavigatedClaimId(claimId);
  };

  // Handle back navigation - pop from history
  const handleBackToClaim = () => {
    if (claimHistory.length > 0) {
      const previousClaim = claimHistory[claimHistory.length - 1];
      setClaimHistory((prev) => prev.slice(0, -1));
      setSelectedRow(previousClaim);
    }
  };

  // Update selectedRow when navigated claim data is fetched
  React.useEffect(() => {
    if (navigatedClaimData && !isNavigatedClaimFetching) {
      setSelectedRow(navigatedClaimData);
      setNavigatedClaimId(null); // Clear navigation state
    }
  }, [navigatedClaimData, isNavigatedClaimFetching]);

  // Clear history when modal is closed
  const handleCloseModal = () => {
    setSelectedRow(null);
    setClaimHistory([]);
  };

  // Fetch data - filter by payment types
  const { data, isLoading, isError, error } = useGetClaimsQuery({
    accountId,
    search: search || undefined,
    startDate: dateRange[0] || undefined,
    endDate: dateRange[1] || undefined,
    sortField,
    sortOrder,
    page,
    pageSize,
    chargeBelowContractRateFilter,
    minDifferencePercent,
    paymentTypes: paymentTypes.length > 0 ? paymentTypes : undefined,
    resolutionFilter,
    classificationFilter:
      classificationFilter === "all" ? undefined : classificationFilter,
    clearPayerFilter: clearPayerFilter || undefined,
  });

  // Show error toast
  React.useEffect(() => {
    if (isError) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: (error as any)?.data?.message || "Failed to load claims",
      });
    }
  }, [isError, error]);

  // Currency formatter
  const formatCurrency = (cents: number | null): string => {
    if (cents === null || cents === undefined) return "-";
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Template functions
  const dateTemplate = (
    rowData: IClaim,
    field: "patientDOB" | "serviceDate"
  ) => {
    const date = rowData[field];
    return date ? parseDate(date) : "-";
  };

  const currencyTemplate = (
    rowData: IClaim,
    field:
      | "chargeAmount"
      | "expectedAmount"
      | "claimAmount"
      | "amountDifference"
  ) => {
    return formatCurrency(rowData[field]);
  };

  // Total Charges template with badge for charge below contract rate
  const chargeAmountTemplate = (rowData: IClaim) => {
    const formattedAmount = formatCurrency(rowData.chargeAmount);
    const isChargeBelowContract =
      rowData.category === CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE;

    if (!isChargeBelowContract) {
      return formattedAmount;
    }

    return (
      <div className="flex flex-column align-items-start gap-1">
        <span>{formattedAmount}</span>
        <Tag
          value={CATEGORY_LABELS[CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE]}
          severity={
            CATEGORY_COLORS[CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE]
          }
          icon="pi pi-info-circle"
        />
      </div>
    );
  };

  const nullableTemplate = (value: string | null) => {
    return value || "-";
  };

  // Variance % template - calculate percentage difference
  const variancePercentTemplate = (rowData: IClaim) => {
    if (
      rowData.expectedAmount === null ||
      rowData.amountDifference === null ||
      rowData.expectedAmount === 0
    ) {
      return "-";
    }

    // Formula: (Math.abs(amountDifference) / expectedAmount) * 100
    const percentage =
      (Math.abs(rowData.amountDifference) / rowData.expectedAmount) * 100;

    return `${percentage.toFixed(1)}%`;
  };

  // Helper function to format case signature with line breaks (4 codes per line)
  const formatCaseSignatureWithLineBreaks = (caseSignature: string) => {
    // Split by comma to get array of "CODE:qty" items
    const items = caseSignature.split(",");

    // Group into chunks of 4
    const chunks: string[][] = [];
    for (let i = 0; i < items.length; i += 4) {
      chunks.push(items.slice(i, i + 4));
    }

    // Return JSX with each chunk on its own line
    return (
      <>
        {chunks.map((chunk, index) => (
          <div key={index}>{chunk.join(",")}</div>
        ))}
      </>
    );
  };

  // Patient name link template - opens modal
  const patientNameTemplate = (rowData: IClaim) => {
    const handleClick = () => {
      setSelectedRow(rowData);
      setModalVisible(true);
    };

    return (
      <CaseSignatureLink onClick={handleClick}>
        {rowData.patientName}
      </CaseSignatureLink>
    );
  };

  // Case signature template - plain text with line breaks
  const caseSignatureTemplate = (rowData: IClaim) => {
    return formatCaseSignatureWithLineBreaks(rowData.caseSignature);
  };

  // Accuracy status template - shows icon based on allowable accuracy
  const accuracyStatusTemplate = (rowData: IClaim) => {
    if (!rowData.precalculatedAllowable) {
      return <span>-</span>;
    }

    const { allowableAccuracyStatus, missingCodes } =
      rowData.precalculatedAllowable;

    if (allowableAccuracyStatus) {
      return (
        <span
          title="Complete - All procedure codes found"
          style={{ color: "#28a745" }}
        >
          <i className="pi pi-check-circle" style={{ fontSize: "1.2rem" }} />
        </span>
      );
    }

    const missingCount = missingCodes?.length || 0;
    return (
      <span
        title={`Incomplete - ${missingCount} code(s) missing`}
        style={{ color: "#ffc107" }}
      >
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "1.2rem" }}
        />
      </span>
    );
  };

  // ===== CLAIM-SIDE TEMPLATES (from 835 PR adjustments) =====
  // Note: 835 adjustment amounts are in dollars (not cents) - use formatDollars

  // Claim patient responsibility (deductible + copay + coinsurance)
  const claimPatientOOPTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.totalOOP > 0 ? formatDollars(resp.totalOOP) : "-";
  };

  // Claim deductible (PR adjustment code 1)
  const claimDeductibleTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.deductible > 0 ? formatDollars(resp.deductible) : "-";
  };

  // Claim coinsurance (PR adjustment code 2)
  const claimCoinsuranceTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.coinsurance > 0 ? formatDollars(resp.coinsurance) : "-";
  };

  // Claim copay (PR adjustment code 3)
  const claimCopayTemplate = (rowData: IClaim) => {
    const resp = extractClaimPatientResponsibility(rowData.lineItems);
    return resp.copay > 0 ? formatDollars(resp.copay) : "-";
  };

  // ===== ESTIMATE-SIDE TEMPLATES (from matched Order) =====
  // Note: All estimate values are filtered to FACILITY procedures only to match 835 claim scope

  // Helper to get FACILITY procedures only
  const getFacilityProcedures = (pricingBreakdown: any): any[] => {
    if (!pricingBreakdown?.procedureBreakdown?.length) return [];
    return pricingBreakdown.procedureBreakdown.filter(
      (proc: any) => proc.specialty === "FACILITY"
    );
  };

  // Estimate allowed amount (sum of amountInCents from FACILITY procedures)
  // Note: pricingBreakdown.totalInCents is patient OOP, NOT allowed amount
  const estimateAllowedTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const pricingBreakdown = rowData.order.gfeQuote.pricingBreakdown as any;
    const facilityProcedures = getFacilityProcedures(pricingBreakdown);

    if (facilityProcedures.length === 0) return "-";

    const allowedAmount = facilityProcedures.reduce(
      (sum: number, procedure: any) => {
        return sum + (procedure.amountInCents ?? 0);
      },
      0
    );

    return formatCurrency(allowedAmount);
  };

  // Estimate patient OOP (sum of owedByPatient.total from FACILITY procedures)
  const estimateOOPTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const pricingBreakdown = rowData.order.gfeQuote.pricingBreakdown as any;
    const facilityProcedures = getFacilityProcedures(pricingBreakdown);

    if (facilityProcedures.length === 0) return "-";

    const totalOOP = facilityProcedures.reduce(
      (sum: number, procedure: any) => {
        const { owedByPatient } = procedure;
        return sum + (owedByPatient?.total ?? 0);
      },
      0
    );

    return formatCurrency(totalOOP);
  };

  // Estimate patient deductible (sum of owedByPatient.deductible from FACILITY procedures)
  const estimateDeductibleTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const pricingBreakdown = rowData.order.gfeQuote.pricingBreakdown as any;
    const facilityProcedures = getFacilityProcedures(pricingBreakdown);

    if (facilityProcedures.length === 0) return "-";

    const totalDeductible = facilityProcedures.reduce(
      (sum: number, procedure: any) => {
        const { owedByPatient } = procedure;
        return sum + (owedByPatient?.deductible ?? 0);
      },
      0
    );

    return formatCurrency(totalDeductible);
  };

  // Estimate patient coinsurance (sum of owedByPatient.coinsurance from FACILITY procedures)
  const estimateCoinsuranceTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const pricingBreakdown = rowData.order.gfeQuote.pricingBreakdown as any;
    const facilityProcedures = getFacilityProcedures(pricingBreakdown);

    if (facilityProcedures.length === 0) return "-";

    const totalCoinsurance = facilityProcedures.reduce(
      (sum: number, procedure: any) => {
        const { owedByPatient } = procedure;
        return sum + (owedByPatient?.coinsurance ?? 0);
      },
      0
    );

    return formatCurrency(totalCoinsurance);
  };

  // Estimate patient copay (sum of owedByPatient.copay from FACILITY procedures)
  const estimateCopayTemplate = (rowData: IClaim) => {
    if (!rowData.order?.gfeQuote) return "-";
    const pricingBreakdown = rowData.order.gfeQuote.pricingBreakdown as any;
    const facilityProcedures = getFacilityProcedures(pricingBreakdown);

    if (facilityProcedures.length === 0) return "-";

    const totalCopay = facilityProcedures.reduce(
      (sum: number, procedure: any) => {
        const { owedByPatient } = procedure;
        return sum + (owedByPatient?.copay ?? 0);
      },
      0
    );

    return formatCurrency(totalCopay);
  };

  // Coinsurance percent = coinsurance / (claim amount – deductible)
  // Uses claim-side values (from 835 PR adjustments) in dollars
  const coinsurancePercentTemplate = (rowData: IClaim) => {
    // Get claim-side values (in dollars from 835)
    const claimResp = extractClaimPatientResponsibility(rowData.lineItems);

    // Get claim amount (in cents) - this is the allowable/paid amount from 835
    if (!rowData.claimAmount) return "-";

    const claimAmountInDollars = rowData.claimAmount / 100;

    // Calculate: Coinsurance% = coinsurance / (claimAmount - deductible)
    const coinsurancePercent = calculateOOPPercent(
      claimResp.coinsurance, // Just coinsurance portion
      claimAmountInDollars,
      claimResp.deductible
    );

    if (coinsurancePercent === null) return "-";
    return `${coinsurancePercent.toFixed(1)}%`;
  };

  // Match confidence indicator
  const matchConfidenceTemplate = (rowData: IClaim) => {
    if (!rowData.order || rowData.orderMatchConfidence === null) {
      return <span>-</span>;
    }

    const confidence = rowData.orderMatchConfidence ?? 0;
    let color: string;
    let title: string;

    if (confidence >= 0.9) {
      color = "#28a745"; // green
      title = `High confidence (${(confidence * 100).toFixed(0)}%)`;
    } else if (confidence >= 0.7) {
      color = "#ffc107"; // yellow
      title = `Medium confidence (${(confidence * 100).toFixed(0)}%)`;
    } else {
      color = "#dc3545"; // red
      title = `Low confidence (${(confidence * 100).toFixed(0)}%)`;
    }

    return (
      <span title={title} style={{ color }}>
        <i className="pi pi-link" style={{ fontSize: "1.2rem" }} />
      </span>
    );
  };

  // Resolution status template - shows color-coded status badge
  const resolutionStatusTemplate = (rowData: IClaim) => {
    const status = rowData.resolutionStatus;
    const label = RESOLUTION_STATUS_LABELS[status || "null"];
    const severity = RESOLUTION_STATUS_COLORS[status || "null"];

    return <Tag value={label} severity={severity} />;
  };

  // Handle DataTable events
  const onSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder === 1 ? "asc" : "desc");
    setPage(1); // Reset to first page
  };

  const onPage = (event: any) => {
    setPage(event.page + 1); // PrimeReact uses 0-indexed pages
    setPageSize(event.rows);
  };

  // Handle filter changes - reset to page 1
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleDateRangeChange = (e: { value: CalendarValueType }) => {
    const dates = e.value as Date[];
    setDateRange(dates || []);
    setPage(1);
  };

  // TODO: Uncomment when ready to enable category filter
  // const handleCategoriesChange = (value: string[]) => {
  //   setCategories(value);
  //   setPage(1);
  // };

  const handleChargeBelowContractRateFilterChange = (
    value: "include" | "exclude" | "only"
  ) => {
    setChargeBelowContractRateFilter(value);
    setPage(1);
  };

  const handleMinDifferencePercentChange = (value: string) => {
    const numValue = value === "" ? undefined : parseFloat(value);
    setMinDifferencePercent(numValue);
    setPage(1);
  };

  const handleResolutionFilterChange = (
    value: "not_reviewed" | "in_review" | "all"
  ) => {
    setResolutionFilter(value);
    setPage(1);
  };

  const handleClassificationFilterChange = (value: string) => {
    setClassificationFilter(value);
    setPage(1);
  };

  const handlePaymentTypesChange = (value: PaymentType[]) => {
    setPaymentTypes(value);
    setPage(1);
  };

  const handleClearPayerFilterChange = (value: string | null) => {
    setClearPayerFilter(value);
    setPage(1);
  };

  // Handle export button click
  const handleExport = async () => {
    try {
      const result = await exportTrigger({
        accountId,
        search: search || undefined,
        startDate: dateRange[0] || undefined,
        endDate: dateRange[1] || undefined,
        sortField,
        sortOrder,
        chargeBelowContractRateFilter,
        minDifferencePercent,
        paymentTypes: paymentTypes.length > 0 ? paymentTypes : undefined,
        resolutionFilter,
        classificationFilter:
          classificationFilter === "all" ? undefined : classificationFilter,
        clearPayerFilter: clearPayerFilter || undefined,
      }).unwrap();

      // Download the file using the signed URL
      window.open(result.downloadUrl, "_blank");

      toast.current?.show({
        severity: "success",
        summary: "Export Started",
        detail: "Your Excel file is being downloaded",
      });
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Export Failed",
        detail: (err as any)?.data?.message || "Failed to export claims",
      });
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
      </div>
    );
  }

  const claims = data?.data || [];
  const totalRecords = data?.totalCount || 0;

  return (
    <>
      <Toast ref={toast} />

      {/* Filters Row 1: Search, Date Range, Category */}
      <div className="flex gap-3 mb-2 flex-wrap align-items-center">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search patient, payer, account #, claim #, or CPT codes..."
            className="w-20rem"
          />
        </span>

        <div className="flex flex-column gap-2" style={{ minWidth: "250px" }}>
          <Calendar
            selectionMode="range"
            placeholder="Service Date Range"
            value={dateRange}
            onChange={handleDateRangeChange}
            showIcon
            dateFormat="mm/dd/yy"
          />
        </div>

        {/* Category Filter (Payment Type) */}
        <div className="flex align-items-center gap-2">
          <label htmlFor="paymentTypesFilter">Category:</label>
          <MultiSelect
            inputId="paymentTypesFilter"
            value={paymentTypes}
            options={PAYMENT_TYPE_OPTIONS}
            onChange={(e) => handlePaymentTypesChange(e.value)}
            placeholder="Select payment types"
            display="chip"
            style={{ minWidth: "200px" }}
          />
        </div>
      </div>

      {/* Filters Row 2: Status, Classification, Other Filters, Export */}
      <div className="flex gap-3 mb-3 flex-wrap align-items-center justify-content-between">
        <div className="flex gap-3 flex-wrap align-items-center">
          {/* Resolution Status Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="resolutionFilter">Status:</label>
            <Dropdown
              inputId="resolutionFilter"
              value={resolutionFilter}
              options={[...RESOLUTION_FILTER_OPTIONS]}
              onChange={(e) => handleResolutionFilterChange(e.value)}
              placeholder="Select status"
            />
          </div>

          {/* AI Classification Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="classificationFilter">Classification:</label>
            <Dropdown
              inputId="classificationFilter"
              value={classificationFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Not Analyzed", value: "not_analyzed" },
                ...(classificationsData || []).map((c) => ({
                  label: `${c.code} - ${c.title}`,
                  value: c.code,
                })),
              ]}
              onChange={(e) => handleClassificationFilterChange(e.value)}
              placeholder="Select classification"
              style={{ minWidth: "180px" }}
            />
          </div>

          {/* Clear Payer Filter */}
          <div className="flex align-items-center gap-2">
            <label htmlFor="clearPayerFilter">Clear Payer:</label>
            <Dropdown
              inputId="clearPayerFilter"
              value={clearPayerFilter}
              options={[
                { label: "All", value: null },
                ...(clearPayersData || []).map((payer) => ({
                  label: payer,
                  value: payer,
                })),
              ]}
              onChange={(e) => handleClearPayerFilterChange(e.value)}
              placeholder="Select payer"
              showClear
              style={{ minWidth: "180px" }}
            />
          </div>

          <div className="flex align-items-center gap-2">
            <label htmlFor="chargeBelowContractRateFilter">
              Charges Below Contract Rate:
            </label>
            <Dropdown
              inputId="chargeBelowContractRateFilter"
              value={chargeBelowContractRateFilter}
              options={[
                { label: "Include", value: "include" },
                { label: "Exclude", value: "exclude" },
                { label: "Only", value: "only" },
              ]}
              onChange={(e) =>
                handleChargeBelowContractRateFilterChange(e.value)
              }
              placeholder="Select filter"
            />
          </div>

          <div className="flex align-items-center gap-2">
            <label htmlFor="minDifferencePercent">Min Difference %:</label>
            <InputText
              id="minDifferencePercent"
              type="number"
              value={minDifferencePercent?.toString() ?? ""}
              onChange={(e) => handleMinDifferencePercentChange(e.target.value)}
              className="w-8rem"
              step="1"
            />
          </div>
        </div>

        {/* Export Button */}
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          className="p-button-success"
          onClick={handleExport}
          loading={isExporting}
          disabled={isLoading || totalRecords === 0}
        />
      </div>

      {/* Data Table */}
      <CompactDataTable
        value={claims}
        lazy
        paginator
        first={(page - 1) * pageSize}
        rows={pageSize}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder === "asc" ? 1 : -1}
        rowsPerPageOptions={[10, 25, 50]}
        stripedRows
        responsiveLayout="scroll"
        emptyMessage={data?.message || "No claims found."}
      >
        <Column
          field="patientName"
          header="Patient Name"
          sortable
          body={patientNameTemplate}
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="serviceDate"
          header="Service Date"
          sortable
          body={(rowData) => dateTemplate(rowData, "serviceDate")}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="claimPayerName"
          header="Payer Name"
          sortable
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="claimNetworkStatus"
          header="Network Status"
          body={(rowData) => nullableTemplate(rowData.claimNetworkStatus)}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="clearPayerName"
          header="Matched Payer"
          body={(rowData) => nullableTemplate(rowData.clearPayerName)}
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="chargeAmount"
          header="Total Charges"
          sortable
          body={chargeAmountTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="claimAmount"
          header="Claim Amount"
          sortable
          body={(rowData) => currencyTemplate(rowData, "claimAmount")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="expectedAmount"
          header="Expected Amount"
          sortable
          body={(rowData) => currencyTemplate(rowData, "expectedAmount")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="amountDifference"
          header="Difference"
          sortable
          body={(rowData) => currencyTemplate(rowData, "amountDifference")}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header="Variance"
          body={variancePercentTemplate}
          sortable
          sortField="variancePercent"
          style={{ minWidth: "7rem" }}
        />
        <Column
          field="caseSignature"
          header="Case Signature"
          body={caseSignatureTemplate}
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="allowableAccuracyStatus"
          header="Accuracy"
          sortable
          body={accuracyStatusTemplate}
          style={{ minWidth: "6rem", textAlign: "center" }}
        />
        {/* Claim-side columns from 835 PR adjustments */}
        <Column
          header="Claim Copay"
          body={claimCopayTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Claim Deductible"
          body={claimDeductibleTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Claim Coinsurance"
          body={claimCoinsuranceTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Coinsurance %"
          body={coinsurancePercentTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Claim Pt OOP"
          body={claimPatientOOPTemplate}
          style={{ minWidth: "8rem" }}
        />
        {/* Estimate-side columns from matched Order */}
        <Column
          header="Est. Allowed"
          body={estimateAllowedTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Est. Copay"
          body={estimateCopayTemplate}
          style={{ minWidth: "7rem" }}
        />
        <Column
          header="Est. Deductible"
          body={estimateDeductibleTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Est. Coinsurance"
          body={estimateCoinsuranceTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Est. Pt OOP"
          body={estimateOOPTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Match"
          body={matchConfidenceTemplate}
          style={{ minWidth: "5rem", textAlign: "center" }}
        />
        <Column
          field="resolutionStatus"
          header="Status"
          body={resolutionStatusTemplate}
          style={{ minWidth: "8rem" }}
        />
      </CompactDataTable>

      {/* Claim Details Modal */}
      {selectedRow && (
        <ClaimDetailsModal
          visible={modalVisible}
          onHide={() => {
            setModalVisible(false);
            handleCloseModal();
          }}
          accountId={accountId}
          claim={selectedRow}
          onNavigateToClaim={handleNavigateToClaim}
          onBackToClaim={
            claimHistory.length > 0 ? handleBackToClaim : undefined
          }
          isNavigating={isNavigatedClaimFetching}
        />
      )}
    </>
  );
}

export default ClaimsList;
